import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper from '../chart_helpers/AxisHelper'
import SizeHelper from '../chart_helpers/SizeHelper'
import { categoricalExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import { tweenDentedRect } from '../utils/pathUtils'
import { select as d3_select } from 'd3-selection'
import { formatNumber } from '../utils/formatUtils'
import { interpolate as d3_interpolate } from 'd3-interpolate'
import { isDarkMode } from '../utils/configUtils'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils'
import { interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'

const MAX_BAR_WIDTH = 40
const MANUAL_BAR_WIDTH_STEPS = 10
export default class SegmentedBarChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
    this.zeroBased = true
  }
  static defaultConfig = {
    /**
     * {-EN-} Minimum height of each bar (in pixels).
     * {-ES-} Altura mínima de cada barra (en píxeles).
     */
    percentageMode: {
      type: [String],
      default: 'independent',
      options: ['aggregate', 'independent'],
      exposed: true,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    minBarHeight: {
      type: [Number],
      default: 0,
      exposed: true,
    },
    manualBarWidth: {
      type: [Number, String, null],
      options: Array.from(Array(MANUAL_BAR_WIDTH_STEPS))
        .map((_, i) => [i + 1, (i + 1).toString()])
        .flat(),
      default: null,
      exposed: true,
    },
    horizontal: {
      type: Boolean,
      default: true,
      exposed: false,
      nonEditable: true,
    },
    showDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    iconDict: {
      type: [Object, Array, null],
      default: null,
      exposed: true,
    },
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    showLabelCategories: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    showValues: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    labelValue: {
      type: [String],
      default: 'percent',
      options: ['none', 'abs', 'percent'],
      exposed: true,
    },
    labelOffsets: {
      type: [Object, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} If set to `true`, sector values will be show as a percentage of the total.
     * {-ES-} Si se establece en `true`, los valores del sector se mostrarán como un porcentaje del total.
     */
    showPercentage: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} Format for the percentages.
     * {-ES-} Formato para los porcentajes.
     */
    percentageFormat: {
      type: [String],
      default: '[N]0,0.0',
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    AxisHelper: {
      zoom: {
        nonEditable: true,
      },
      scroll: {
        nonEditable: true,
      },
      showScrollbar: {
        nonEditable: true,
      },
      x: {
        scale: {
          value: 'band',
          exposed: false,
          nonEditable: true,
        },
        side: {
          value: 'top',
          exposed: false,
          nonEditable: true,
        },
        showTickValues: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        tickOrientation: {
          exposed: false,
          nonEditable: true,
        },
        tickWrap: {
          exposed: false,
          nonEditable: true,
        },
        valueFormat: {
          exposed: false,
          nonEditable: true,
        },
        grid: {
          exposed: false,
          nonEditable: true,
        },
        unit: {
          exposed: false,
          nonEditable: true,
        },
        trimThreshold: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        labelMaxWidth: {
          exposed: false,
          nonEditable: true,
        },
        getExtent: function (data) {
          return categoricalExtent(data)
        },
      },
      y: {
        grid: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        side: {
          value: 'right',
          exposed: false,
          nonEditable: true,
        },
        showTickValues: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        scale: {
          value: 'linear',
          exposed: false,
          nonEditable: true,
        },
        tickOrientation: {
          exposed: false,
          nonEditable: true,
        },
        tickWrap: {
          exposed: false,
          nonEditable: true,
        },
        trimThreshold: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        getExtent: function (data) {
          // FIJAMOS 0,100 o no!?!?!
          return [0, 100]
          // let extent, extentParts
          // extentParts = continuousExtentParts(data, d => [d.y0, d.y1])
          // // console.log("extent parts",extentParts)
          // extent = continuousExtent(extentParts)

          // // console.log("bar extent bef",extent)
          // extent = cleanCollisions(this.axes.y, extent, extentParts)
          // // console.log("bar extent aft",extent)
          // return extent
        },
      },
    },
    /**
     * {-EN-} Show/hide icon for each sector label.
     * {-ES-} Mostrar/ocultar icono para cada etiqueta de sector.
     */
    showLabelIcons: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    TooltipBehavior: {
      getItemOffset: function ($el) {
        let original = $el.getBoundingClientRect()
        return {
          x: original.x + original.width / 2,
          y: original.top, //- 5,
        }
      },
      getHtml: function (d) {
        let formatter = (value, format) => {
          let match = this.matchFormatString(format || '')
          let numberFormat = match && match[2]
          let thouDel = this.config.thousandsDelimiter
          let decDel = this.config.decimalDelimiter
          return formatNumber(value, numberFormat, thouDel, decDel)
        }
        if (this.data.length > 1)
          return `
          <div class="tip-value important">${this.formatTooltipValue(d.value, 'y')}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.parentId }
            )}; margin-right: 3px"></span>
            ${d.parentId}
          </div>
          <div class="secondary">${
            this.config.showPercentage
              ? '<div class="secondary">' +
                formatter(d.percent, this.config.percentageFormat) +
                '%</div>'
              : ''
          }</div>
            `
        else
          return `
        <div class="tip-value important">${this.formatTooltipValue(d.value, 'y')}</div>
        <div class="secondary">
            ${d.id}
          </div>
        `
      },
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper]
  formatter(value, format) {
    let match = this.matchFormatString(format || '')
    let numberFormat = match && match[2]
    let thouDel = this.config.thousandsDelimiter
    let decDel = this.config.decimalDelimiter
    return formatNumber(value, numberFormat, thouDel, decDel)
  }
  checkConsistency() {
    return true
  }
  beforeHelpers() {
    this.margin = { top: 20, right: 20, left: 20, bottom: 20 }
  }
  draw() {
    let longDir, getTranslate, getOffset, getShort //,shortDir

    longDir = 'height'
    // shortDir = 'width'

    getTranslate = (long, short) => {
      return `translate(${short},${long})`
    }

    let offsetValue
    if (this.axes.y.func.scale().domain()[0] < 0 && this.axes.y.func.scale().domain()[1] > 0)
      offsetValue = 0
    else offsetValue = this.axes.y.func.scale().domain()[0]

    getOffset = v => Math.min(v, this.axes.y.func.scale()(offsetValue))
    getShort = v => Math.abs(this.axes.y.func.scale()(offsetValue) - v)

    let barWidth = MAX_BAR_WIDTH

    this.config.manualBarWidth !== null &&
      (barWidth = (this.config.manualBarWidth / (MANUAL_BAR_WIDTH_STEPS / 2)) * barWidth)
    let barMargin = (this.axes.x.func.scale().bandwidth() - barWidth) / 2

    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    let drawItem
    let stackPadding = 2
    let self = this
    drawItem = (sel, status) => {
      sel.attr('transform', d => {
        let barPos
        if (status === 'enter' || status === 'exit') barPos = this.axes.y.func.scale()(0)
        else barPos = this.axes.y.func.scale()(this.config.horizontal ? d.y0 : d.y1)
        return getTranslate(
          this.axes.x.func.scale()(d.id) + barMargin,
          barPos + (this.config.horizontal ? 0 : stackPadding / 2)
        )
      })

      sel
        .select('.item-path')
        //.attr('transform', `translate(0,${barLabelOfsset})`)
        .attr('fill', d => {
          if (d.parentId === '_total_')
            return isDarkMode(this.config, this._parent.accessibilityOptions)
              ? 'rgb(68, 68, 68)'
              : 'rgb(246, 250, 252)'
          else return this.color({ id: d.parentId })
        })
      if (sel.select('.item-path').attrTween) {
        sel.select('.item-path').attrTween('d', function (d) {
          let barLength
          if (status === 'enter' || status === 'exit') barLength = 0
          else
            barLength =
              getShort(self.axes.y.func.scale()(d.safeValue)) -
              (d.isFirstLayer ? stackPadding : stackPadding)
          barLength = Math.max(barLength, 0)

          return tweenDentedRect.call(this, d, {
            barLength,
            barWidth,
            longDir,
            scale: self.axes.y.func.scale(),
          })
        })
        sel.select('.item-path').attrTween('fill', function (d) {
          return fillTween.bind(this)({ ...d, id: d.parentId })
        })
      }
    }

    const ICON_HEIGHT = 23
    const ICON_WIDTH = 23
    const ICON_OFFSET = 5
    let getIcon = d => {
      return (
        !!this.config.showLabelIcons &&
        this.config.ItemsHelper.colorDict &&
        this.config.ItemsHelper.colorDict[d.parentId]?.icon?.icon &&
        this.config.ItemsHelper.colorDict[d.parentId]?.icon
      )
    }

    this.itemsEnter.append('path').attr('class', 'item-path')
    // HAy que pintar lo "viejos" primero para assegurar que se mantiene el orden de colores...
    this.itemsTrans.call(drawItem)
    this.itemsEnter.call(drawItem, 'enter')
    this.itemsEnterTrans.call(drawItem)
    this.itemsExitTrans.style('opacity', 0)
    if (this.parentItemsExitTrans) this.parentItemsExitTrans.style('opacity', 0)

    let labelPath = function (d) {
      const textNode = d3_select(this).select('text').node()
      const y = d.y || 10
      /*
      const x = let
      let angle = getLabelAngle(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      let start, joint, end
      if (isLabelOffsetCustomized(d)) {
        start = [center[0] + Math.cos(angle) * r0, center[1] + Math.sin(angle) * r0]
        joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
        end = [joint[0] + 15 * sign, joint[1]]
      } else {
        start = [center[0] + Math.cos(angle) * r0, center[1] + Math.sin(angle) * r0]
        joint = [(d.y * Math.cos(angle)) / Math.sin(angle), d.y]
        end = [d.x - 3 * sign, d.y]
        //un poco cutre hacerlo con sort...
        let jointRange = [start[0], end[0]]
        jointRange.sort((a, b) => a - b)
        joint[0] = Math.min(Math.max(joint[0], jointRange[0]), jointRange[1])
      }
      */
      //return `M ${start} L ${joint} L ${[end]}`
      return `M ${[0, 0]} L ${[0, y]} L ${[y > 0 ? 10 : -10, y]}`
    }

    let labelTransform = function (d, offset = [0, 0]) {
      const y = d.y || 10
      const xGap = 15
      let translate = [(xGap + offset[0]) * (isLabelPlacedBottom(d) ? 1 : -1), y + offset[1]]
      return `translate(${translate})`
    }
    function pathTween(a) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelPath(i(t))
      }
    }
    function transTween(a, offset) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelTransform(i(t), offset)
      }
    }

    const getLabelValue = d => {
      switch (this.config.labelValue) {
        case 'abs':
          return this.valueWithUnit(d.value)
        case 'percent':
          return this.formatter(d.safeValue, this.config.percentageFormat) + '%'
        default:
          return null
      }
    }

    const isLabelPlacedBottom = d => {
      return this.config.labelOffsets?.[d.parentId]?.position !== 'top' && (!d.y || d.y > 0)
    }

    const getXLabelTranslate = d => {
      const totalBarLength =
        getShort(this.axes.y.func.scale()(d.safeValue)) -
        (d.isFirstLayer ? stackPadding : stackPadding)
      const labelOffset = this.config.labelOffsets?.[d.parentId]?.offset
      if (labelOffset) {
        return (totalBarLength * labelOffset) / 100
      }
      return isLabelPlacedBottom(d) ? 0 : totalBarLength
    }

    //Check if has icon or text in order to append label
    const showLabel = d => {
      return (
        (this.config.showLabels &&
          (this.config.showLabelCategories || this.config.labelValue !== 'none')) ||
        (this.config.showLabelIcons && !!getIcon(d))
      )
    }

    const getLabelHtml = d => {
      let labelCategory = this.config.showLabels && this.config.showLabelCategories ? d.label : null
      let labelValue =
        this.config.showLabels && this.config.labelValue !== 'none' ? getLabelValue(d) : null
      if (labelCategory)
        return (
          `<tspan alignment-baseline="middle">${d.label}${labelValue ? ': ' : ''}</tspan>` +
          (labelValue
            ? `<tspan alignment-baseline="middle" style="opacity: 1; font-weight: bold;">${labelValue}</tspan>`
            : '')
        )
      else if (labelValue) {
        return `<tspan alignment-baseline="middle" style="opacity: 1; font-weight: bold;">${labelValue}</tspan>`
      }
      return ''
    }

    let ghostLabelsContainer = this.content.selectAll('.ghost-labels-container').data([null])
    const ghostLabelsContainerEnter = ghostLabelsContainer
      .enter()
      .append('g')
      .attr('class', 'ghost-labels-container')
      .style('display', 'none')
    ghostLabelsContainer = ghostLabelsContainer.merge(ghostLabelsContainerEnter)

    let ghostLabelItems = ghostLabelsContainer.selectAll('.ghost-label-item').data(
      this.data.map(
        d => d.value[0],
        d => d.id
      )
    )
    const ghostLabelItemsEnter = ghostLabelItems
      .enter()
      .append('g')
      .attr('class', 'ghost-label-item')

    ghostLabelItems.exit().remove()
    ghostLabelItems = ghostLabelItems.merge(ghostLabelItemsEnter)
    ghostLabelItems.attr('transform', d => {
      //const labelPosition = this.config.labelOffsets?.[d.parentId]?.position
      const yTranslate =
        (this.config.horizontal ? d.y0 : d.y1) + (this.config.horizontal ? 0 : stackPadding / 2)
      return getTranslate(
        this.axes.x.func.scale()(d.id) + barMargin,
        this.axes.y.func.scale()(yTranslate)
      )
    })

    //Reference with idilic positions and helps label to decide its placements
    let ghostLabels = ghostLabelItems.selectAll('.ghost-label').data(
      d => {
        return showLabel(d)
          ? [{ ...d, customId: d.parentId + this.config.shape, label: d.parentId }]
          : []
      },
      dd => dd.customId
    )
    let ghostLabelsEnter = ghostLabels.enter().append('g').attr('class', 'ghost-label')

    ghostLabels.exit().remove()
    ghostLabelsEnter.append('path').attr('stroke', 'currentColor').attr('fill', 'none')

    ghostLabels = ghostLabels.merge(ghostLabelsEnter)

    ghostLabels.select('path').attr('d', labelPath)

    const labelsOffset = 10
    ghostLabels.attr(
      'transform',
      d =>
        `translate(${getXLabelTranslate(d)}, ${
          (barWidth + labelsOffset) *
          (this.config.labelOffsets?.[d.parentId]?.position === 'top' ? -1 : 1)
        })`
    )

    let ghostLabelsText = ghostLabels.selectAll('text').data(
      d => (this.config.showLabelCategories || this.config.labelValue !== 'none' ? [d] : []),
      d => d.id
    )
    ghostLabelsText.exit().remove()
    const ghostLabelsTextEnter = ghostLabelsText
      .enter()
      .append('text')
      .attr('fill', 'currentColor')
      .html(d => d.parentId)
      .attr('alignment-baseline', 'middle')
      .attr('font-family', 'santander-microtext-embedded')

    ghostLabelsText = ghostLabelsText.merge(ghostLabelsTextEnter)
    ghostLabelsText
      .attr(
        'font-size',
        this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
          ? '18px'
          : '14px'
      )
      .attr('text-anchor', d => (isLabelPlacedBottom(d) ? 'start' : 'end'))

    ghostLabelsText.html(d => getLabelHtml(d))

    ghostLabelsText.attr('transform', d => {
      return labelTransform(d, getIcon(d)?.icon ? [ICON_WIDTH + 4, 0] : undefined)
    })

    //Labels
    let labels = this.items
      .merge(this.itemsEnter)
      .selectAll('.label')
      .data(
        d =>
          showLabel(d)
            ? [{ ...d, customId: d.parentId + this.config.shape, label: d.parentId }]
            : [],
        dd => dd.customId
      )
    let labelsEnter = labels.enter().append('g').attr('class', 'label')
    labels.exit().remove()
    this.itemsExitTrans.select('.label').attr('opacity', 0)

    labelsEnter
      .append('path')
      .attr('stroke', 'currentColor')
      .attr('fill', 'none')
      .style('opacity', 0)

    labels = labels.merge(labelsEnter)

    //Set labels placements based on ghost labels
    const setLabelsPlacements = () => {
      const ghostLabelsBoundingsGrouped = {
        top: [],
        bottom: [],
      }
      ghostLabels.each(function (label) {
        const key = !isLabelPlacedBottom(label) ? 'top' : 'bottom'
        const boundings = this.getBoundingClientRect()
        ghostLabelsBoundingsGrouped[key].push({ ...label, boundings })
      })

      const contentClientRect = this.$root.getBoundingClientRect()

      if (!this.config.labelOffsets) {
        ghostLabelsBoundingsGrouped.top = ghostLabelsBoundingsGrouped.top.filter(labelBounding => {
          if (labelBounding.boundings.x - labelBounding.boundings.width < contentClientRect.x) {
            ghostLabelsBoundingsGrouped.bottom.push(labelBounding)
            return false
          }
          return true
        })
        ghostLabelsBoundingsGrouped.bottom = ghostLabelsBoundingsGrouped.bottom.filter(
          labelBounding => {
            if (
              labelBounding.boundings.x + labelBounding.boundings.width >
              contentClientRect.x + contentClientRect.width
            ) {
              ghostLabelsBoundingsGrouped.top.push(labelBounding)
              return false
            }
            return true
          }
        )
      }
      //SORT ARRAYS
      ghostLabelsBoundingsGrouped.top.sort((a, b) => a.boundings.x - b.boundings.x)
      ghostLabelsBoundingsGrouped.bottom.sort((a, b) => a.boundings.x - b.boundings.x)

      //let totalOverflowsBottom = 0
      //let totalOverflowsTop = 0

      /*
      //Move from bottom to top
      if (overflowsBottom !== -1) {
        totalOverflowsBottom = ghostLabelsBoundingsGrouped.bottom.length - overflowsBottom
        const overflowItems = ghostLabelsBoundingsGrouped.bottom.splice(
          totalOverflowsBottom + 1,
          ghostLabelsBoundingsGrouped.bottom.length
        )
        ghostLabelsBoundingsGrouped.top = [...ghostLabelsBoundingsGrouped.top, ...overflowItems]
      }
      //Move from top to bottom
      if (overflowsTop !== -1) {
        totalOverflowsTop = ghostLabelsBoundingsGrouped.top.length - overflowsTop
        const overflowItems = ghostLabelsBoundingsGrouped.top.splice(
          totalOverflowsTop,
          ghostLabelsBoundingsGrouped.top.length
        )
        ghostLabelsBoundingsGrouped.bottom = [
          ...ghostLabelsBoundingsGrouped.bottom,
          ...overflowItems,
        ]
      }
      */
      //const totalOverflows = overflows !== -1 ? ghostLabelsBoundings.length - overflows : 0

      const overlapsTop = ghostLabelsBoundingsGrouped.top.find((labelBounding, i) => {
        if (i === 0) return false
        const previousLabel = ghostLabelsBoundingsGrouped.top[i - 1]
        return (
          previousLabel.boundings.y === labelBounding.boundings.y &&
          previousLabel.boundings.x + previousLabel.boundings.width > labelBounding.boundings.x
        )
      })
      const overlapsBottom = ghostLabelsBoundingsGrouped.bottom.find((labelBounding, i) => {
        if (i === 0) return false
        const previousLabel = ghostLabelsBoundingsGrouped.bottom[i - 1]
        return (
          previousLabel.boundings.y === labelBounding.boundings.y &&
          previousLabel.boundings.x + previousLabel.boundings.width > labelBounding.boundings.x
        )
      })

      let topIndex = 0
      let bottomIndex = 0
      labels.each(label => {
        const isPlacedTop = ghostLabelsBoundingsGrouped.top.find(d => d.parentId === label.parentId)
        label.isPlacedTop = !!isPlacedTop
        const placeKey = isPlacedTop ? 'top' : 'bottom'
        const i = isPlacedTop ? topIndex : bottomIndex
        const ghostLabelsBoundings = ghostLabelsBoundingsGrouped[placeKey]
        const overlaps = isPlacedTop ? overlapsTop : overlapsBottom

        if (isPlacedTop) {
          //Placement from top right to left
          label.y = (ghostLabelsBoundings[0].boundings.height * (overlaps ? i : 0) + 10) * -1
          label.textAnchor = 'end'
        }
        //Placement from bottom left to right
        else if (overlaps) {
          label.y =
            ghostLabelsBoundings[0].boundings.height * (ghostLabelsBoundings.length - i - 1) + 10
          label.textAnchor = 'start'
        } else {
          label.y = null
          label.textAnchor = !isPlacedTop ? 'start' : 'end'
        }
        if (isPlacedTop) topIndex++
        else bottomIndex++
      })
    }

    setLabelsPlacements()

    let labelsTrans = labels.transition().duration(1000)

    labels.attr('transform', d => {
      return `translate(${getXLabelTranslate(d)}, ${
        isLabelPlacedBottom(d) ? barWidth + labelsOffset : -labelsOffset
      })`
    })

    let labelsText = labels.selectAll('text').data(
      d => (this.config.showLabelCategories || this.config.labelValue !== 'none' ? [d] : []),
      d => d.id
    )
    labelsText.exit().remove()
    const labelsTextEnter = labelsText
      .enter()
      .append('text')
      .attr('fill', 'currentColor')
      .html(d => d.parentId)
      .attr('alignment-baseline', 'middle')
      .attr('font-family', 'santander-microtext-embedded')

    labelsText = labelsText.merge(labelsTextEnter)
    labelsText.attr(
      'font-size',
      this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
        ? '18px'
        : '14px'
    )

    labelsText.html(d => getLabelHtml(d))

    //labelsTrans.select('text').attrTween('transform', transTween)
    //segmentedBarLabels(labels)

    labelsTrans.style('opacity', 1)
    labelsTrans.select('path').attrTween('d', pathTween).style('opacity', 1)
    labelsTrans
      .select('text')
      .attr('text-anchor', d => d.textAnchor || 'start')
      .attrTween('transform', function (d) {
        return transTween.call(this, d, getIcon(d)?.icon ? [ICON_WIDTH + 4, 0] : undefined)
      })

    //ICONS
    let iconGroup = labels
      .selectAll('.icon')
      .data(d => (getIcon(d)?.icon ? [{ ...d, id: d.parentId, ...getIcon(d) }] : []))

    iconGroup.exit().remove()
    let iconGroupEnter = iconGroup.enter().append('g').attr('class', 'icon')
    iconGroup = iconGroup.merge(iconGroupEnter)
    iconGroup.html(d => (d ? d.icon.svg : null))
    let iconGroupSvg = iconGroup.select('svg')
    //Hay q hardcodear nuevos ids en los defs y en los use para hacer unicas las modificaciones
    iconGroupSvg
      .select('defs')
      .select('path')
      .attr('id', function (d) {
        return this.getAttribute('id') ? this.getAttribute('id') + `donut-chart-icon-${d.id}` : null
      })
    iconGroupSvg.select('use').attr('xlink:href', function (d) {
      return this.getAttribute('xlink:href')
        ? this.getAttribute('xlink:href') + `donut-chart-icon-${d.id}`
        : null
    })
    iconGroupSvg.selectAll('path, polygon').attr('fill', function () {
      let d = d3_select(this.parentNode).datum()
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })
    iconGroupSvg.attr('height', `${ICON_HEIGHT}px`).attr('width', `${ICON_WIDTH}px`)
    iconGroupSvg.select('use').attr('fill', function (d) {
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })

    labelsTrans.select('.icon').attrTween('transform', function (d) {
      return transTween.call(this, d, [
        isLabelPlacedBottom(d) ? 0 : ICON_WIDTH,
        (ICON_HEIGHT / 2) * -1 - 2,
      ])
    })
  }
}
